<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import {
  FileUp, List, Plus, X,
} from 'lucide-vue-next';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue3-toastify';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import Pagination from '~/components/Pagination/Pagination.vue';
import useAuth from '~/store/auth/useAuth';

import CessionModal from '../components/CessionModal.vue';
import useCreateCession from '../queries/useCreateCession';
import useFilterFinancings from '../queries/useFilterFinancings';
import useImportFinancings from '../queries/useImportFinancings';
import useSecuritizations from '../queries/useSecuritizations';
import { validatePremium } from '../schemas/premiumValidation';
import type { CessionCategoryEnum } from '../services/capitalMarketsService';
import downloadCsvExample from '../utils/downloadCsvExample';

const route = useRoute();
const { data: user } = useAuth();

const category = computed<CessionCategoryEnum>(() => {
  const cat = route.query.category as string;
  return (cat === 'overturning' ? 'overturning' : 'new_cession') as CessionCategoryEnum;
});

const isOverturning = computed(() => category.value === 'overturning');
const pageTitle = computed(() => (isOverturning.value ? 'Criar Tombamento' : 'Criar Cessão'));
const entityLabel = computed(() => (isOverturning.value ? 'Tombamento' : 'Cessão'));
const entityLabelLower = computed(() => entityLabel.value.toLowerCase());
const pageDescription = computed(() => {
  const article = isOverturning.value ? 'um novo' : 'uma nova';
  return `Crie ${article} ${entityLabelLower.value} importando financiamentos`;
});

// Step management
const currentStep = ref<'csv' | 'manual' | 'selection'>('selection');
const selectedMethod = ref<'csv' | 'manual' | null>(null);

// CSV flow
const csvFile = ref<File | null>(null);
const csvFinancingIds = ref<number[]>([]);
const csvError = ref<string>('');

// Manual flow
const filterParams = ref({
  financing_ids: [] as number[],
  page: 1,
  size: 10,
  status: isOverturning.value ? ['available', 'ceded'] : ['available'],
});

// Update status when isOverturning changes
watch(isOverturning, (newValue) => {
  filterParams.value.status = newValue ? ['available', 'ceded'] : ['available'];
  filterParams.value.page = 1; // Reset to first page when status changes
});

const filterIdsInput = ref('');
const selectedFinancingIds = ref<Set<number>>(new Set());

// Import new financings
const showImportForm = ref(false);
const importIdsInput = ref('');
const importResult = ref<null | { failed: number[]; imported: number[] }>(null);

// Final form
const securitizationName = ref('');
const premium = ref<number>(0);
const premiumError = ref<null | string>(null);
const showCessionModal = ref(false);

// Validate premium
watch(premium, (newValue) => {
  premiumError.value = validatePremium(newValue);
});

// Queries
const createCessionMutation = useCreateCession();
const { data: securitizations, isLoading: isLoadingSecuritizations } = useSecuritizations();
const {
  data: financingsData,
  isLoading: isLoadingFinancings,
  refetch: refetchFinancings,
} = useFilterFinancings(filterParams);
const importFinancingsMutation = useImportFinancings();

const securitizationOptions = computed(() => {
  if (!securitizations.value?.items) return [];
  return securitizations.value.items.map((s) => ({
    label: s.name.toUpperCase(),
    value: s.name,
  }));
});

const currentPage = computed(() => filterParams.value.page);
const pageSize = computed(() => filterParams.value.size);
const hasNext = computed(() => {
  if (!financingsData.value) return false;
  return financingsData.value.page < financingsData.value.pages;
});

const financings = computed(() => financingsData.value?.items || []);
const allFinancingIds = computed(() => financings.value.map((f) => f.solfacil_financing_id));
const allSelected = computed(() => {
  if (allFinancingIds.value.length === 0) return false;
  return allFinancingIds.value.every((id) => selectedFinancingIds.value.has(id));
});
const someSelected = computed(() => {
  if (allFinancingIds.value.length === 0) return false;
  return !allSelected.value && allFinancingIds.value.some((id) => selectedFinancingIds.value.has(id));
});

function backToSelection() {
  currentStep.value = 'selection';
  selectedMethod.value = null;
  // eslint-disable-next-line no-use-before-define
  resetForms();
}

function closeImportForm() {
  showImportForm.value = false;
  importIdsInput.value = '';
  importResult.value = null;
}

function formatDate(dateString: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateString;
  }
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    available: 'Disponível',
    ceded: 'Cedido',
  };
  return labels[status] || status;
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    pf: 'Pessoa Física',
    pj: 'Pessoa Jurídica',
  };
  return labels[type] || type.toUpperCase();
}

function handleClearFilter() {
  filterIdsInput.value = '';
  filterParams.value.financing_ids = [];
  filterParams.value.page = 1;
}

async function handleCreateCession() {
  if (!securitizationName.value) {
    toast.error('Por favor, selecione a securitizadora.');
    return;
  }

  const userEmail = user.value?.keycloack.email;
  if (!userEmail) {
    toast.error('Erro ao obter informações do usuário.');
    return;
  }

  let financingIds: number[] = [];

  if (currentStep.value === 'csv') {
    financingIds = csvFinancingIds.value;
  } else {
    financingIds = Array.from(selectedFinancingIds.value);
  }

  if (financingIds.length === 0) {
    toast.error('Por favor, selecione ao menos um financiamento.');
    return;
  }

  const today = new Date().toISOString().split('T')[0];

  const premiumValue = premium.value;

  const payload = [{
    category: category.value,
    ceded_at: today,
    premium: premiumValue,
    securitization_name: securitizationName.value,
    solfacil_financing_ids: financingIds,
    user: userEmail,
  }];

  try {
    await createCessionMutation.mutateAsync(payload);
    showCessionModal.value = false;
  } catch (error) {
    // Error já tratado no hook
  }
}

function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  if (!file.name.endsWith('.csv')) {
    csvError.value = 'Por favor, selecione um arquivo CSV válido.';
    return;
  }

  csvFile.value = file;
  csvError.value = '';

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const text = e.target?.result as string;
      const lines = text.split('\n').map((line) => line.trim()).filter((line) => line);
      const ids = lines.map((line) => {
        const num = Number.parseInt(line, 10);
        if (Number.isNaN(num)) {
          throw new Error(`ID inválido: ${line}`);
        }
        return num;
      });

      if (ids.length === 0) {
        throw new Error('O arquivo CSV está vazio.');
      }

      csvFinancingIds.value = ids;
      csvError.value = '';
      toast.success(`${ids.length} IDs carregados do CSV.`);
    } catch (error: any) {
      csvError.value = error.message || 'Erro ao processar o arquivo CSV.';
      csvFinancingIds.value = [];
    }
  };
  reader.readAsText(file);
}

function handleFilterApply() {
  const input = filterIdsInput.value.trim();
  if (!input) {
    filterParams.value.financing_ids = [];
  } else {
    const ids = input
      .split(/[\n,]+/)
      .map((id) => id.trim())
      .filter((id) => id)
      .map((id) => Number.parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));
    filterParams.value.financing_ids = ids;
  }
  filterParams.value.page = 1;
}

async function handleImportFinancings() {
  const input = importIdsInput.value.trim();
  if (!input) {
    toast.error('Por favor, insira ao menos um ID de financiamento.');
    return;
  }

  const ids = input
    .split(/[\n,]+/)
    .map((id) => id.trim())
    .filter((id) => id)
    .map((id) => Number.parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));

  if (ids.length === 0) {
    toast.error('Nenhum ID válido encontrado.');
    return;
  }

  try {
    const result = await importFinancingsMutation.mutateAsync({ solfacil_financing_ids: ids });
    importResult.value = result;

    if (result.imported.length > 0) {
      toast.success(`${result.imported.length} financiamento(s) importado(s) com sucesso!`);
      refetchFinancings();
    }

    if (result.failed.length > 0) {
      toast.warning(`${result.failed.length} financiamento(s) falharam na importação.`);
    }
  } catch (error) {
    // Error já tratado no hook
  }
}

function handleOpenCessionModal() {
  if (selectedFinancingIds.value.size === 0) {
    toast.error('Por favor, selecione ao menos um financiamento.');
    return;
  }
  showCessionModal.value = true;
}

function handlePageChange(page: number) {
  filterParams.value.page = page;
}

function handlePageSizeChange(size: number) {
  filterParams.value.size = Math.max(10, size);
  filterParams.value.page = 1;
}

function handleShowImportForm() {
  showImportForm.value = true;
  importResult.value = null;
}

function removeCsvFile() {
  csvFile.value = null;
  csvFinancingIds.value = [];
  csvError.value = '';
}

function resetForms() {
  csvFile.value = null;
  csvFinancingIds.value = [];
  csvError.value = '';
  filterIdsInput.value = '';
  selectedFinancingIds.value.clear();
  showImportForm.value = false;
  importIdsInput.value = '';
  importResult.value = null;
  securitizationName.value = '';
  premium.value = 0;
  premiumError.value = null;
}

function selectMethod(method: 'csv' | 'manual') {
  selectedMethod.value = method;
  currentStep.value = method;
}

function toggleSelectAll() {
  if (allSelected.value) {
    allFinancingIds.value.forEach((id) => selectedFinancingIds.value.delete(id));
  } else {
    allFinancingIds.value.forEach((id) => selectedFinancingIds.value.add(id));
  }
}

function toggleSelectFinancing(id: number) {
  if (selectedFinancingIds.value.has(id)) {
    selectedFinancingIds.value.delete(id);
  } else {
    selectedFinancingIds.value.add(id);
  }
}

onMounted(() => {
  document.title = pageTitle.value;
});
</script>

<template>
  <DashboardLayout :title="pageTitle" :description="pageDescription">
    <ol class="breadcrumb breadcrumb-arrows mb-3" aria-label="breadcrumbs">
      <li class="breadcrumb-item">
        <router-link to="/cessions">Cessão e Tombamento</router-link>
      </li>
      <li class="breadcrumb-item active" aria-current="page">
        <span>{{ pageTitle }}</span>
      </li>
    </ol>

    <!-- Step 1: Selection -->
    <div v-if="currentStep === 'selection'" class="row g-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <h3 class="card-title mb-4">Escolha o método de importação</h3>
            <p class="text-muted mb-4">
              Selecione como deseja importar os financiamentos para esta {{ entityLabelLower }}
            </p>

            <div class="row g-3 justify-content-center">
              <div class="col-12 col-md-5">
                <div
                  class="card card-link card-link-pop cursor-pointer h-100"
                  role="button"
                  tabindex="0"
                  @click="selectMethod('csv')"
                  @keydown.enter="selectMethod('csv')"
                  @keydown.space.prevent="selectMethod('csv')"
                >
                  <div class="card-body text-center py-5">
                    <FileUp class="icon icon-lg text-primary mb-3" :size="48" />
                    <h4 class="card-title">Importar via CSV</h4>
                    <p class="text-muted">
                      Faça upload de um arquivo CSV contendo os IDs dos financiamentos
                    </p>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-5">
                <div
                  class="card card-link card-link-pop cursor-pointer h-100"
                  role="button"
                  tabindex="0"
                  @click="selectMethod('manual')"
                  @keydown.enter="selectMethod('manual')"
                  @keydown.space.prevent="selectMethod('manual')"
                >
                  <div class="card-body text-center py-5">
                    <List class="icon icon-lg text-success mb-3" :size="48" />
                    <h4 class="card-title">Importar Manualmente</h4>
                    <p class="text-muted">
                      Busque e selecione financiamentos disponíveis na base de dados
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: CSV Flow -->
    <div v-else-if="currentStep === 'csv'" class="row g-3">
      <div class="col-12">
        <button type="button" class="btn btn-ghost-secondary mb-3" @click="backToSelection">
          ← Voltar
        </button>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Importação via CSV</h3>
          </div>
          <div class="card-body">
            <!-- CSV Upload -->
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-start">
                <label class="form-label required" for="csvFileInput">Arquivo CSV</label>
                <button
                  type="button"
                  class="btn btn-link px-0"
                  aria-label="Baixar exemplo de arquivo CSV"
                  @click="downloadCsvExample"
                >
                  Baixar exemplo
                </button>
              </div>
              <input
                id="csvFileInput"
                type="file"
                class="form-control"
                accept=".csv"
                @change="handleFileUpload"
              />
              <small class="form-hint">
                O arquivo deve conter uma coluna com IDs de financiamento, um por linha, sem cabeçalho.
              </small>

              <div v-if="csvError" class="alert alert-danger mt-2">
                {{ csvError }}
              </div>

              <div v-if="csvFile && !csvError" class="alert alert-success mt-2">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ csvFile.name }}</strong> - {{ csvFinancingIds.length }} financiamento(s) carregado(s)
                  </div>
                  <button type="button" class="btn btn-sm btn-ghost-danger" @click="removeCsvFile">
                    <X :size="16" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Securitization Select -->
            <div class="mb-3">
              <label class="form-label required" for="securitization_csv">Securitizadora</label>
              <select
                id="securitization_csv"
                v-model="securitizationName"
                class="form-select"
                :disabled="isLoadingSecuritizations || csvFinancingIds.length === 0"
              >
                <option value="">Selecione a securitizadora...</option>
                <option
                  v-for="option in securitizationOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Premium Input -->
            <div class="mb-3">
              <label class="form-label required" for="premium_csv">Ágio (%)</label>
              <input
                id="premium_csv"
                v-model.number="premium"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': premiumError }"
                step="0.01"
                min="-5"
                max="5"
                placeholder="0.00"
                :disabled="csvFinancingIds.length === 0"
              />
              <div v-if="premiumError" class="invalid-feedback">
                {{ premiumError }}
              </div>
            </div>

            <!-- Submit Button -->
            <div class="d-flex gap-2 justify-content-end">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="csvFinancingIds.length === 0 || !securitizationName || createCessionMutation.isPending.value"
                @click="handleCreateCession"
              >
                <span v-if="createCessionMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
                Criar {{ entityLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Manual Flow -->
    <div v-else-if="currentStep === 'manual'" class="row g-3">
      <div class="col-12">
        <button type="button" class="btn btn-ghost-secondary mb-3" @click="backToSelection">
          ← Voltar
        </button>
      </div>

      <!-- Import New Financings Card -->
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3 class="card-title mb-0">Importar Novos Financiamentos</h3>
            <button
              v-if="!showImportForm"
              type="button"
              class="btn btn-sm btn-primary"
              @click="handleShowImportForm"
            >
              <Plus :size="16" class="me-1" />
              Importar
            </button>
          </div>

          <div v-if="showImportForm" class="card-body">
            <div class="mb-3">
              <label class="form-label" for="import_ids">IDs dos Financiamentos</label>
              <textarea
                id="import_ids"
                v-model="importIdsInput"
                class="form-control"
                rows="3"
                placeholder="Digite os IDs separados por vírgula ou quebra de linha&#10;Exemplo: 800367, 683931"
              />
              <small class="form-hint">
                Insira os IDs dos financiamentos que deseja importar, separados por vírgula ou quebra de linha.
              </small>
            </div>

            <div v-if="importResult" class="mb-3">
              <div v-if="importResult.imported.length > 0" class="alert alert-success">
                <strong>Importados com sucesso ({{ importResult.imported.length }}):
                </strong> {{ importResult.imported.join(', ') }}
              </div>

              <div v-if="importResult.failed.length > 0" class="alert alert-danger">
                <strong>Falharam na importação ({{ importResult.failed.length }}):
                </strong> {{ importResult.failed.join(', ') }}
              </div>
            </div>

            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!importIdsInput.trim() || importFinancingsMutation.isPending.value"
                @click="handleImportFinancings"
              >
                <span v-if="importFinancingsMutation.isPending.value" class="spinner-border spinner-border-sm me-2" />
                Importar
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeImportForm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Financings List -->
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title col-6">Financiamentos Disponíveis</h3>
            <div class="col-6 d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="selectedFinancingIds.size === 0"
                @click="handleOpenCessionModal"
              >
                Criar {{ entityLabel }}
              </button>
            </div>
          </div>

          <div class="card-body">
            <!-- Filter -->
            <div class="row mb-3">
              <div class="col-12 col-md-8 mb-2 mb-md-0">
                <label class="form-label" for="filter_ids">Filtrar por IDs</label>
                <textarea
                  id="filter_ids"
                  v-model="filterIdsInput"
                  class="form-control"
                  rows="2"
                  placeholder="Digite os IDs separados por vírgula ou quebra de linha"
                />
              </div>
              <div class="col-12 col-md-4 d-flex align-items-end gap-2">
                <button type="button" class="btn btn-primary" @click="handleFilterApply">
                  Filtrar
                </button>
                <button type="button" class="btn btn-secondary" @click="handleClearFilter">
                  Limpar
                </button>
              </div>
            </div>

            <!-- Selected Count -->
            <div v-if="selectedFinancingIds.size > 0" class="alert alert-info">
              <strong>{{ selectedFinancingIds.size }}</strong> financiamento(s) selecionado(s)
            </div>

            <!-- Loading -->
            <div v-if="isLoadingFinancings" class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
            </div>

            <!-- Table -->
            <div v-else-if="financings.length > 0">
              <div class="table-responsive">
                <table class="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">
                        <label class="form-check">
                          <input
                            id="selectAllCheckbox"
                            type="checkbox"
                            class="form-check-input"
                            :checked="allSelected"
                            :indeterminate="someSelected"
                            @change="toggleSelectAll"
                          />
                          <span class="visually-hidden">Selecionar todos</span>
                        </label>
                      </th>
                      <th>Financiamento ID</th>
                      <th>Cliente</th>
                      <th>CCB</th>
                      <th>Securitizadora</th>
                      <th>Data Formalização</th>
                      <th>Status</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="financing in financings" :key="financing.solfacil_financing_id">
                      <td>
                        <label class="form-check">
                          <input
                            :id="`financing-${financing.solfacil_financing_id}`"
                            type="checkbox"
                            class="form-check-input"
                            :checked="selectedFinancingIds.has(financing.solfacil_financing_id)"
                            @change="toggleSelectFinancing(financing.solfacil_financing_id)"
                          />
                          <span class="visually-hidden">
                            Selecionar financiamento {{ financing.solfacil_financing_id }}
                          </span>
                        </label>
                      </td>
                      <td><strong>{{ financing.solfacil_financing_id }}</strong></td>
                      <td>{{ financing.client_name }}</td>
                      <td>{{ financing.ccb }}</td>
                      <td>{{ financing.external_slug.toUpperCase() }}</td>
                      <td>{{ formatDate(financing.formalization_date) }}</td>
                      <td>
                        <span class="badge bg-success-lt">
                          {{ getStatusLabel(financing.status) }}
                        </span>
                      </td>
                      <td>{{ getTypeLabel(financing.type) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="pagination-wrapper">
                <Pagination
                  :current-page="currentPage"
                  :has-next="hasNext"
                  :page-size="pageSize"
                  :min-page-size="10"
                  @page-change="handlePageChange"
                  @page-size-change="handlePageSizeChange"
                />
              </div>
            </div>
            <!-- Empty State -->
            <div v-else class="text-center py-5 text-muted">
              Nenhum financiamento disponível encontrado.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cession Modal -->
    <CessionModal
      v-model="showCessionModal"
      v-model:securitization-name="securitizationName"
      v-model:premium="premium"
      :securitization-options="securitizationOptions"
      :is-loading-securitizations="isLoadingSecuritizations"
      :selected-financing-ids="selectedFinancingIds"
      :is-loading="createCessionMutation.isPending.value"
      :entity-label="entityLabel"
      @save="handleCreateCession"
    />
  </DashboardLayout>
</template>

<style scoped lang="scss">
.cursor-pointer {
  cursor: pointer;
}

.card-link-pop:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}
@media (max-width: 768px) {
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    overflow-x: auto;
    padding: 0 1rem;
  }

  .pagination-wrapper :deep(.d-flex) {
    flex-wrap: wrap;
    justify-content: center !important;
    padding-right: 0 !important;
    width: 100%;
  }
}

@media (min-width: 769px) {
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
